// Shared, RBAC-gated source of the manager/admin navigation sections
// (Administration, Payroll, Attendance). These sections are NOT in the static
// navigation registry (registry.ts) because their visibility depends on live
// permission checks, role access, product entitlements and the benefits
// settings fetch — none of which the registry models.
//
// Historically the sidebar built these arrays inline, which meant the global
// command palette (⌘K), which only reads `useFilteredNavigation().navigationGroups`,
// could never surface admin/payroll/attendance pages ("payroll" / "loan"
// returned nothing). This module is the single place the sections are built, so
// BOTH the sidebar and the palette index exactly the same RBAC-gated set. RBAC
// is enforced identically for every consumer — the palette can only ever
// under-reveal relative to the sidebar, never over-reveal.
//
// The item-building is a PURE function (`buildManagementNavigation`) over a flat
// `gates` object so it is unit-testable per role without mocking React context;
// the hook just wires the gates from the RBAC/entitlement hooks.
import { useEffect, useMemo, useState } from 'react';
import {
  Users, FolderTree, Calendar, Settings, Wallet, Coins, Rocket, UploadCloud, Bot,
  CalendarDays, Banknote, Briefcase, HeartPulse, Clock, UserMinus, Database,
  ClipboardCheck, CreditCard, FileCheck2, FileBadge, UserPlus, PackageCheck,
  Landmark, Code2, Workflow, BarChart3, Store, ShieldCheck, ScrollText, Receipt,
  Layers, UserCheck, GraduationCap, Activity,
} from 'lucide-react';
import { getBenefitsSettings } from '@/lib/hrApi';
import { useHasPermission } from '@/rbac/useRbac';
import { useEntitlements } from '@/features/entitlements/useEntitlements';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';

export interface ManagementNavItem {
  id: string;
  path: string;
  label: string;
  icon: typeof Users;
}

export interface ManagementNavigation {
  adminItems: ManagementNavItem[];
  payrollItems: ManagementNavItem[];
  attendanceItems: ManagementNavItem[];
  showAdmin: boolean;
  showPayroll: boolean;
  showAttendance: boolean;
}

/** Flat gate inputs — every RBAC/entitlement decision the sections depend on. */
export interface ManagementNavGates {
  // Permissions (write/manage unless noted).
  canUsers: boolean;         // user:read:own-tenant
  canAudit: boolean;         // audit:view
  canAssignRoles: boolean;   // user:role:assign
  canDelegate: boolean;      // leave|expenses|workassignment approve/assign
  canDepartments: boolean;   // department:write
  canLeavePolicies: boolean; // leavepolicy:write
  canCompanySettings: boolean; // tenant:update:own
  canAutomation: boolean;    // integration:manage
  canImport: boolean;        // employee:import
  canMigrate: boolean;       // migration:read
  canAiConfig: boolean;      // ai-config:write
  canWorkforce: boolean;     // workforce:framework:read
  canLearningAdmin: boolean; // learning:manage
  canWorkManage: boolean;    // work:manage (Work Management · Access & Assignment)
  canReports: boolean;       // reports:view
  canApproveChanges: boolean; // employee:change:approve
  canApprovePayments: boolean; // employee:payment:approve
  canConnector?: boolean;       // attendance:connector:read (IT: the connector only)
  // Role access (canonical RBAC).
  isTenantOwner: boolean;
  isManager: boolean;
  isFinance: boolean;
  canViewPayroll: boolean;
  canRunPayroll: boolean;
  canApproveLeave: boolean;
  canManageEmployees: boolean;
  canAdminister: boolean;
  // Entitlements + async settings.
  hasProduct: (p: string) => boolean;       // fails open for un-provisioned tenants
  hasProductStrict: (p: string) => boolean; // only when explicitly enabled
  benefitsEnabled: boolean;                  // any benefits feature on
}

/**
 * Pure builder — given the gate flags, produce the three sections. No hooks, no
 * context: fully deterministic and unit-testable per role.
 */
export function buildManagementNavigation(g: ManagementNavGates): ManagementNavigation {
  const adminItems = [
    g.canDepartments && { id: 'admin-setup', path: 'admin/setup', label: 'Getting Started', icon: Rocket },
    g.canUsers && { id: 'admin-users', path: 'admin/users', label: 'Users & Roles', icon: Users },
    g.canAssignRoles && { id: 'admin-roles', path: 'admin/roles', label: 'Roles & permissions', icon: ShieldCheck },
    g.canAssignRoles && { id: 'admin-scope-policy', path: 'admin/scope-policy', label: 'Management scope', icon: Layers },
    (g.canDelegate || g.canAssignRoles) && { id: 'admin-delegations', path: 'admin/delegations', label: 'Approval delegation', icon: UserCheck },
    g.canAssignRoles && { id: 'admin-delegation-policy', path: 'admin/delegation-policy', label: 'Delegation policy', icon: UserCheck },
    g.canReports && { id: 'admin-reports', path: 'reports', label: 'Reports', icon: BarChart3 },
    g.canDepartments && { id: 'admin-departments', path: 'admin/departments', label: 'Departments', icon: FolderTree },
    g.canDepartments && { id: 'admin-job-titles', path: 'admin/job-titles', label: 'Job Titles', icon: Briefcase },
    g.canImport && { id: 'admin-import', path: 'admin/import', label: 'Import Employees', icon: UploadCloud },
    g.canMigrate && { id: 'admin-migration', path: 'admin/migration', label: 'Migration Center', icon: Database },
    g.canLeavePolicies && { id: 'admin-leave-policies', path: 'admin/leave-policies', label: 'Leave Policies', icon: Calendar },
    g.canLeavePolicies && { id: 'admin-holidays', path: 'admin/holidays', label: 'Holidays', icon: CalendarDays },
    g.canLearningAdmin && g.hasProduct('learning') && { id: 'admin-learning', path: 'admin/learning', label: 'Learning', icon: GraduationCap },
    g.canWorkManage && g.hasProduct('work') && { id: 'admin-work-access', path: 'admin/work-access', label: 'Work Management · Access & Assignment', icon: ClipboardCheck },
    g.canWorkManage && g.hasProduct('work') && { id: 'admin-workload', path: 'admin/workload', label: 'Work Management · Workload & Health', icon: Activity },
    (g.isTenantOwner || g.canAdminister) && { id: 'admin-document-requirements', path: 'admin/document-requirements', label: 'Document Requirements', icon: FileBadge },
    (g.isTenantOwner || g.canAdminister) && { id: 'admin-document-compliance', path: 'admin/document-compliance', label: 'Document Compliance', icon: FileBadge },
    g.canAiConfig && { id: 'admin-ai', path: 'admin/ai-assistant', label: 'AI Assistant', icon: Bot },
    (g.canApproveChanges || g.isManager) && { id: 'admin-change-approvals', path: 'admin/change-approvals', label: 'Change Approvals', icon: FileCheck2 },
    g.canApprovePayments && { id: 'admin-payment-approvals', path: 'admin/payment-approvals', label: 'Payment Approvals', icon: Landmark },
    g.isManager && { id: 'admin-lifecycle', path: 'admin/lifecycle', label: 'Onboarding & Exits', icon: UserPlus },
    g.isTenantOwner && { id: 'admin-lifecycle-packages', path: 'admin/lifecycle-packages', label: 'Lifecycle Packages', icon: PackageCheck },
    g.benefitsEnabled && (g.canCompanySettings || g.canViewPayroll) && { id: 'admin-benefits', path: 'benefits', label: 'Benefits', icon: HeartPulse },
    g.canUsers && { id: 'admin-compliance', path: 'admin/compliance', label: 'Data Governance', icon: ClipboardCheck },
    g.canAudit && { id: 'admin-audit', path: 'admin/audit', label: 'Audit Log', icon: ScrollText },
    g.canCompanySettings && { id: 'admin-billing', path: 'admin/billing', label: 'Billing', icon: CreditCard },
    (g.canCompanySettings || g.isFinance) && { id: 'admin-payments', path: 'settings/payments', label: 'Payment provider', icon: Banknote },
    (g.canCompanySettings || g.isFinance) && { id: 'admin-payouts', path: 'payouts', label: 'Payouts', icon: Landmark },
    g.canWorkforce && { id: 'admin-workforce', path: 'admin/workforce', label: 'Workforce', icon: Workflow },
    (g.isTenantOwner || g.canCompanySettings) && g.hasProduct('developer_platform') &&
      { id: 'admin-developer', path: 'admin/developer', label: 'Developer Platform', icon: Code2 },
    g.canCompanySettings && { id: 'admin-settings', path: 'settings', label: 'Company Settings', icon: Settings },
    g.canAutomation && { id: 'admin-automation', path: 'settings/automation', label: 'Automation', icon: Clock },
  ].filter(Boolean) as ManagementNavItem[];

  const payrollItems = (g.canViewPayroll
    ? [
        { id: 'payroll-runs', path: 'payroll', label: 'Payroll Runs', icon: Wallet },
        { id: 'payroll-finance', path: 'payroll/finance', label: 'Finance & Filings', icon: Landmark },
        g.canRunPayroll && { id: 'payroll-salaries', path: 'payroll/salaries', label: 'Bulk Salaries', icon: Banknote },
        g.canRunPayroll && { id: 'payroll-import', path: 'payroll/import', label: 'Import Salaries', icon: UploadCloud },
        (g.canRunPayroll || g.isFinance) && g.hasProduct('credit') && { id: 'payroll-loans', path: 'payroll/loans', label: 'Loans & Advances', icon: Coins },
        (g.canRunPayroll || g.isFinance) && g.hasProductStrict('commission') && { id: 'commission', path: 'commission', label: 'Contractor & External Pay', icon: Briefcase },
        (g.canRunPayroll || g.isFinance) && g.hasProductStrict('expenses') && { id: 'expense-approvals', path: 'expense-approvals', label: 'Expense Approvals', icon: Receipt },
        (g.canRunPayroll || g.isFinance) && { id: 'marketplace', path: 'marketplace', label: 'Marketplace', icon: Store },
        g.canRunPayroll && { id: 'payroll-final', path: 'payroll/final-settlement', label: 'Final Settlement', icon: UserMinus },
        g.canRunPayroll && { id: 'payroll-settings', path: 'payroll/settings', label: 'Payroll Settings', icon: Settings },
      ].filter(Boolean) as ManagementNavItem[]
    : []);

  const attendanceItems = ((g.canApproveLeave || g.canManageEmployees)
    ? [
        { id: 'workforce-overview', path: 'workforce-overview', label: 'Overview', icon: Users },
        { id: 'attendance', path: 'attendance', label: 'Attendance', icon: Clock },
        { id: 'work-assignments', path: 'work-assignments', label: 'Work Assignments', icon: Briefcase },
      ] as ManagementNavItem[]
    // IT / connector administrators reach only the connector view.
    : g.canConnector
      ? [{ id: 'attendance', path: 'attendance', label: 'Attendance connector', icon: Clock }] as ManagementNavItem[]
      : []);

  return {
    adminItems,
    payrollItems,
    attendanceItems,
    showAdmin: adminItems.length > 0,
    // Payroll module gate matches the gateway (403s /payroll/* without the product).
    showPayroll: payrollItems.length > 0 && g.hasProduct('payroll'),
    showAttendance: attendanceItems.length > 0,
  };
}

/**
 * Builds the Administration / Payroll / Attendance nav sections from the same
 * RBAC + entitlement gates the sidebar has always used. Pure derivation of hook
 * state — safe to call from any component under the auth/tenant providers.
 */
export function useManagementNavigation(): ManagementNavigation {
  // Company Administration (Model A) — gated by the MANAGE (write) permission for
  // each tool, so it only shows to those who can actually administer it.
  const canUsers = useHasPermission('user:read:own-tenant');
  const canAudit = useHasPermission('audit:view');
  const canAssignRoles = useHasPermission('user:role:assign');
  // Anyone who can approve something may delegate those approvals (self-service).
  const canDelegateLeave = useHasPermission('leave:approve');
  const canDelegateExpenses = useHasPermission('expenses:approve');
  const canDelegateWork = useHasPermission('workassignment:assign');
  const canDepartments = useHasPermission('department:write');
  const canLeavePolicies = useHasPermission('leavepolicy:write');
  const canCompanySettings = useHasPermission('tenant:update:own');
  // HR reaches Automation for the two imprest rows only (the page filters);
  // a full admin reaches it for every curated automation.
  const canAutomation = useHasPermission('integration:manage') || useHasPermission('expenses:imprest:configure');
  const canImport = useHasPermission('employee:import');
  const canMigrate = useHasPermission('migration:read');
  const canAiConfig = useHasPermission('ai-config:write');
  const canWorkforce = useHasPermission('workforce:framework:read');
  const canLearningAdmin = useHasPermission('learning:manage');
  const canWorkManage = useHasPermission('work:manage');
  const canReports = useHasPermission('reports:view');
  const canApproveChanges = useHasPermission('employee:change:approve');
  const canApprovePayments = useHasPermission('employee:payment:approve');
  const canConnector = useHasPermission('attendance:connector:read');

  const {
    isTenantOwner, isManager, isFinance,
    canViewPayroll, canRunPayroll, canApproveLeave, canManageEmployees, canAdminister,
  } = useRoleAccess();
  const { hasProduct, hasProductStrict } = useEntitlements();

  // Benefits visibility. Admin "Benefits" shows when any feature is on.
  const [benefitsEnabled, setBenefitsEnabled] = useState(false);
  useEffect(() => {
    getBenefitsSettings()
      .then((s) => {
        const anyFeature = !!s.benefits_enabled || !!s.allowances_enabled || !!s.deductions_enabled;
        setBenefitsEnabled(anyFeature);
      })
      .catch(() => { setBenefitsEnabled(false); });
  }, []);

  return useMemo(() => buildManagementNavigation({
    canUsers, canAudit, canAssignRoles,
    canDelegate: canDelegateLeave || canDelegateExpenses || canDelegateWork,
    canDepartments, canLeavePolicies, canCompanySettings, canAutomation, canImport,
    canMigrate, canAiConfig, canWorkforce, canLearningAdmin, canWorkManage, canReports,
    canApproveChanges, canApprovePayments, canConnector,
    isTenantOwner, isManager, isFinance,
    canViewPayroll, canRunPayroll, canApproveLeave, canManageEmployees, canAdminister,
    hasProduct, hasProductStrict, benefitsEnabled,
  }), [
    canUsers, canAudit, canAssignRoles, canDelegateLeave, canDelegateExpenses, canDelegateWork,
    canDepartments, canLeavePolicies, canCompanySettings, canAutomation, canImport, canMigrate,
    canAiConfig, canWorkforce, canLearningAdmin, canWorkManage, canReports, canApproveChanges, canApprovePayments, canConnector,
    isTenantOwner, isManager, isFinance, canViewPayroll, canRunPayroll, canApproveLeave,
    canManageEmployees, canAdminister, hasProduct, hasProductStrict, benefitsEnabled,
  ]);
}
